import { getSiteVersion, onSiteVersionChange } from "../../utils";
import type { SiteVersion } from "../../utils";
import { Popup } from "./Popup";
import { PopupType, popupSelector } from "./popup-config";
import type { PopupElement } from "./popup-config";
import { ScrollLock } from "./ScrollLock";
import { SheetSwipe } from "./SheetSwipe";

export class PopupManager {
    private readonly popups = new Map<PopupElement, Popup>();
    private readonly scrollLock = new ScrollLock();

    private listenersAbortController: AbortController | null = null;
    private siteVersion = getSiteVersion();
    private isPointerDownStartedOnBackdrop = false;
    private shouldIgnoreNextClickAfterSwipe = false;
    private scrollAnimationFrameId = 0;

    init(rootElement: Element): void {
        if (rootElement.matches(".popup")) {
            this.register(rootElement as PopupElement);
        }

        rootElement
            .querySelectorAll<PopupElement>(".popup")
            .forEach(element => this.register(element));
    }

    register(element: PopupElement): Popup {
        this.bindGlobalListeners();

        let popup = this.popups.get(element);

        if (!popup) {
            popup = new Popup(element, () => this.syncScrollLock());
            this.popups.set(element, popup);
            this.syncScrollLock();
        }

        return popup;
    }

    getById(popupId: string): Popup | null {
        const element = document.querySelector<PopupElement>(popupSelector(popupId));

        return element ? this.register(element) : null;
    }

    destroy(): void {
        this.listenersAbortController?.abort();
        this.listenersAbortController = null;

        cancelAnimationFrame(this.scrollAnimationFrameId);
        this.scrollAnimationFrameId = 0;

        this.popups.forEach(popup => popup.destroy());
        this.popups.clear();
        this.scrollLock.setActive(false);
    }

    private destroyDisconnectedPopups(): void {
        for (const [element, popup] of this.popups) {
            if (!element.isConnected) {
                popup.destroy();
                this.popups.delete(element);
            }
        }
    }

    private getConnectedPopups(): Popup[] {
        this.destroyDisconnectedPopups();

        return [...this.popups.values()];
    }

    private syncScrollLock(): void {
        const isAnyPopupLockingPageScroll = this.getConnectedPopups().some(popup => popup.isLockingPageScroll);

        this.scrollLock.setActive(isAnyPopupLockingPageScroll);
    }

    private bindGlobalListeners(): void {
        if (this.listenersAbortController !== null) {
            return;
        }

        this.listenersAbortController = new AbortController();
        const { signal } = this.listenersAbortController;

        document.addEventListener("pointerdown", this.handleDocumentPointerDown, { signal });
        document.addEventListener("click", this.handleDocumentClick, { signal });
        document.addEventListener("scroll", this.handleDocumentScroll, { passive: true, signal });
        onSiteVersionChange(this.handleSiteVersionChange, { signal });
    }

    private readonly handleDocumentPointerDown = (event: PointerEvent): void => {
        this.shouldIgnoreNextClickAfterSwipe = false;
        this.isPointerDownStartedOnBackdrop = false;

        const target = event.target;

        if (!(target instanceof Element)) {
            return;
        }

        const popupElement = target.closest<PopupElement>(".popup");
        const popup = popupElement ? this.popups.get(popupElement) : undefined;

        if (!popup) {
            return;
        }

        this.isPointerDownStartedOnBackdrop = popup.isEventOnBackdrop(event);

        SheetSwipe.startIfPossible(popup, event, isClosed => {
            this.shouldIgnoreNextClickAfterSwipe = isClosed;
        });
    };

    private readonly handleDocumentClick = (event: MouseEvent): void => {
        if (this.shouldIgnoreNextClickAfterSwipe) {
            this.shouldIgnoreNextClickAfterSwipe = false;
            event.preventDefault();
            return;
        }

        const target = event.target;

        if (!(target instanceof Element)) {
            return;
        }

        const clickedButton = target.closest<HTMLElement>(".popup-button[data-popup-id]");
        const clickedButtonPopup = clickedButton ? this.getById(clickedButton.dataset.popupId!) : null;

        this.closeDropdownsOutsideOf(target, clickedButtonPopup);
        this.toggleByButton(clickedButton, clickedButtonPopup);
        this.dismissByStaticBackdrop(target);
        this.delegateClickToClosestPopup(event, target);

        this.isPointerDownStartedOnBackdrop = false;
    };

    private closeDropdownsOutsideOf(target: Element, exceptPopup: Popup | null): void {
        for (const popup of this.getConnectedPopups()) {
            const isOpenDropdown = popup.isOpen && popup.type === PopupType.DROPDOWN;
            const isClickInsidePopup = popup.element.contains(target);

            if (isOpenDropdown && popup !== exceptPopup && !isClickInsidePopup) {
                popup.close();
            }
        }
    }

    private toggleByButton(button: HTMLElement | null, popup: Popup | null): void {
        if (button === null || popup === null) {
            return;
        }

        popup.toggle();

        if (popup.isOpen) {
            button.classList.add("active");
        }
    }

    private dismissByStaticBackdrop(target: Element): void {
        const backdropElement = target.closest(".popup-backdrop");
        const popupElement = backdropElement?.previousElementSibling;

        if (popupElement instanceof HTMLElement) {
            this.popups.get(popupElement)?.dismiss();
        }
    }

    private delegateClickToClosestPopup(event: MouseEvent, target: Element): void {
        const popupElement = target.closest<PopupElement>(".popup");

        if (popupElement === null) {
            return;
        }

        this.popups.get(popupElement)?.handleClick(event, this.isPointerDownStartedOnBackdrop);
    }

    private readonly handleDocumentScroll = (): void => {
        if (this.scrollAnimationFrameId) {
            return;
        }

        this.scrollAnimationFrameId = requestAnimationFrame(() => {
            this.scrollAnimationFrameId = 0;
            this.getConnectedPopups().forEach(popup => popup.handlePageScroll());
        });
    };

    private readonly handleSiteVersionChange = (currentSiteVersion: SiteVersion): void => {
        if (currentSiteVersion === this.siteVersion) {
            return;
        }

        this.siteVersion = currentSiteVersion;
        this.getConnectedPopups().forEach(popup => popup.handleSiteVersionChange());
        this.syncScrollLock();
    };
}

export const popupManager = new PopupManager();
